import { randomUUID } from 'crypto';
import { UIBlock } from './uiblock';

/**
 * Strip the streaming buffer down to the clean, user-facing answer for use in
 * conversation history. Removes whole source-agent blocks (marker AND the
 * name|intent content between them), answer-component JSON, and progress markers
 * — so history carries the answer, not the agent's internal plumbing (which may
 * contain a wrongly-guessed entity/uid).
 */
function cleanAssistantText(raw: string): string {
  if (!raw) return '';
  return raw
    // Whole source-agent blocks, including the intent text between the markers.
    .replace(/__SB_\w+_(?:START|MSG)__[\s\S]*?__SB_END__/g, ' ')
    // Answer-component payloads.
    .replace(/__ANSWER_COMPONENT_START__[\s\S]*?__ANSWER_COMPONENT_END__/g, ' ')
    // Any stray/orphaned markers (truncated blocks, generation/text-complete).
    .replace(/__SB_\w+_(?:START|MSG)_?_?/g, '')
    .replace(/__SB_END__/g, '')
    .replace(/__ANSWER_COMPONENT(?:_START|_END)?__/g, '')
    .replace(/__TEXT_COMPLETE__/g, '')
    .replace(/__COMPONENT_GENERATION_START__/g, '')
    // Collapse whitespace left behind.
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Thread represents a conversation thread containing multiple UIBlocks
 * Each UIBlock in a thread represents a user question and assistant response pair
 */
export class Thread {
  private id: string;
  private uiblocks: Map<string, UIBlock>;
  private createdAt: Date;

  /**
   * Creates a new Thread instance
   * @param id - Optional custom ID, generates UUID if not provided
   */
  constructor(id?: string) {
    this.id = id || randomUUID();
    this.uiblocks = new Map();
    this.createdAt = new Date();
  }

  /**
   * Get the thread ID
   */
  getId(): string {
    return this.id;
  }

  /**
   * Add a UIBlock to the thread
   */
  addUIBlock(uiblock: UIBlock): void {
    this.uiblocks.set(uiblock.getId(), uiblock);
  }

  /**
   * Get a UIBlock by ID
   */
  getUIBlock(id: string): UIBlock | undefined {
    return this.uiblocks.get(id);
  }

  /**
   * Get all UIBlocks in the thread
   */
  getUIBlocks(): UIBlock[] {
    return Array.from(this.uiblocks.values());
  }

  /**
   * Get UIBlocks as a Map
   */
  getUIBlocksMap(): Map<string, UIBlock> {
    return new Map(this.uiblocks);
  }

  /**
   * Remove a UIBlock by ID
   */
  removeUIBlock(id: string): boolean {
    return this.uiblocks.delete(id);
  }

  /**
   * Check if UIBlock exists
   */
  hasUIBlock(id: string): boolean {
    return this.uiblocks.has(id);
  }

  /**
   * Get number of UIBlocks in the thread
   */
  getUIBlockCount(): number {
    return this.uiblocks.size;
  }

  /**
   * Clear all UIBlocks from the thread
   */
  clear(): void {
    this.uiblocks.clear();
  }

  /**
   * Get creation timestamp
   */
  getCreatedAt(): Date {
    return this.createdAt;
  }

  /**
   * Get conversation context from recent UIBlocks (excluding current one)
   * Returns formatted string with previous questions and component summaries
   * @param limit - Maximum number of previous UIBlocks to include (default: 5)
   * @param currentUIBlockId - ID of current UIBlock to exclude from context (optional)
   * @returns Formatted conversation history string
   */
  getConversationContext(limit: number = 5, currentUIBlockId?: string): string {
    if (limit === 0) {
      return '';
    }

    // Get all UIBlocks sorted by creation time (oldest first)
    const allBlocks = Array.from(this.uiblocks.values())
      .filter(block => !currentUIBlockId || block.getId() !== currentUIBlockId)
      .sort((a, b) => a.getCreatedAt().getTime() - b.getCreatedAt().getTime());

    if (allBlocks.length === 0) {
      return '';
    }

    // Take the last N blocks (most recent)
    const recentBlocks = allBlocks.slice(-limit);

    // Format as conversation history
    const contextLines: string[] = [];

    recentBlocks.forEach((block, index) => {
      const questionNum = index + 1;
      const question = block.getUserQuestion();
      const metadata = block.getComponentMetadata();
      const textResponse = block.getTextResponse();

      // Determine what was generated and build appropriate response
      let assistantResponse = '';

      // Check if component was generated (metadata exists and has meaningful content)
      const hasComponent = metadata && Object.keys(metadata).length > 0 && metadata.type;

      // Check if text response was generated
      const hasTextResponse = textResponse && textResponse.trim().length > 0;

      const responseParts: string[] = [];

      if (hasComponent) {
        // Component was generated - show brief summary only.
        // Do NOT include full props JSON — it contains SQL queries, queryIds,
        // component configs, and layout data that bloats conversation history
        // by ~13K tokens per previous question. The main agent only needs to
        // know what was asked and what type of answer was generated.
        const parts: string[] = [];

        if (metadata.type) {
          parts.push(`Component Type: ${metadata.type}`);
        }
        if (metadata.name) {
          parts.push(`Name: ${metadata.name}`);
        }
        if (metadata.description) {
          parts.push(`Description: ${metadata.description}`);
        }

        // Extract only the layout title from props (if available) — not the full JSON
        const layoutTitle = metadata.props?.config?.title;
        if (layoutTitle) {
          parts.push(`Dashboard Title: ${layoutTitle}`);
        }

        responseParts.push(parts.join('\n'));
      }

      if (hasTextResponse) {
        // Clean the raw stream text FIRST, then trim. The raw textResponse is the
        // streaming buffer — it contains whole source-agent blocks
        // (__SB_..._START__<name>|<intent>__SB_END__), answer-component JSON, and
        // progress markers. The old code stripped only the marker tokens and
        // truncated to 500 chars, which (a) left the source-agent INTENT text
        // inline (propagating any wrong entity/uid the agent guessed) and (b) cut
        // before the actual answer. Removing the whole blocks first yields the
        // clean, user-facing answer.
        const cleaned = cleanAssistantText(textResponse);
        if (cleaned) {
          const trimmedText = cleaned.length > 800
            ? cleaned.substring(0, 800) + '...[truncated]'
            : cleaned;
          responseParts.push(trimmedText);
        }
      }

      if (responseParts.length > 0) {
        // Join both component and text response if both exist
        assistantResponse = responseParts.join('\n');
      } else {
        // Nothing was generated
        assistantResponse = 'No response generated';
      }

      contextLines.push(`User:\n ${question}`);
      contextLines.push(`Assistant:\n ${assistantResponse}`);
      contextLines.push('---'); // Empty line for readability
    });

    return contextLines.join('\n').trim();
  }

  /**
   * Convert Thread to JSON-serializable object
   */
  toJSON(): Record<string, any> {
    return {
      id: this.id,
      uiblocks: Array.from(this.uiblocks.values()).map(block => block.toJSON()),
      createdAt: this.createdAt.toISOString(),
    };
  }
}
